// Explore maps (1.6) as pure SVG strings, with no DOM or three.js, so Node tests can check them.
// Both maps keep the player in the middle with north (+z) up and −x to the right, matching the default
// camera: a place at (x, z) is drawn at (p.x − x, p.z − z). Every name is escaped. Clicks are delegated
// through data-act like the rest of the UI (ui.js → act()); closest('[data-act]') finds SVG groups too.
import { escapeHtml } from './util.js';
import { REGION, biomeAt, homeRegion } from './discovery-terrain.js';
import { STATIONS, PLOT, STALL } from './discovery-state.js';
import { LANDMARKS } from './expansion-rules.js';

export const FRIEND_COLOUR = '#9fd8ff';
export const MAP_RANGE = 80;
const INK = '#0b2a3a', PATH = '#e9d39a', GOLD = '#ffe27a';
const esc = (s) => escapeHtml(s ?? '');
const num = (v) => Math.round(v * 100) / 100; // short, stable SVG numbers
const deg = (rad) => num((rad * 180) / Math.PI);
const placed = (o) => !!o && Number.isFinite(o.x) && Number.isFinite(o.z);
const inTown = (o) => homeRegion(Math.floor(o.x / REGION), Math.floor(o.z / REGION));
const steps = (n) => `${n} step${n === 1 ? '' : 's'}`;

// "ava" → "A": the minimap has room for one letter.
export const initial = (name) => ([...String(name ?? '').trim()][0] || '?').toUpperCase();

// Short map labels: the words on the town lessons' signs and the Quest panel's landmark buttons.
const LESSON_LABELS = { numbers: 'Numbers', words: 'Words', crystals: 'Patterns', farm: 'Fractions', clock: 'Time', builder: 'Shapes' };
const LANDMARK_LABELS = { cave: 'Crystal Mine', farm: 'Garden', sky: 'Sky Garden', moon: 'Moonflower', perch: 'Ember', railway: 'Railway', machines: 'Machines' };
// Town places the journal's buttons already lead to (navigate ids).
export const MAP_PLACES = [
  { id: 'table', name: 'Craft & Trade', icon: '🛠️', x: STALL.x, z: STALL.z },
  { id: 'grove', name: 'Wood & Stone', icon: '🪵', x: 10, z: -15 },
];
// Expansion landmarks with a marker in the world (the vault is underground, inside the mine).
export const MAP_LANDMARKS = Object.entries(LANDMARKS)
  .filter(([id]) => id !== 'vault')
  .map(([id, l]) => ({ id, name: l.name, label: LANDMARK_LABELS[id], icon: l.icon, x: l.x, z: l.z }));

// Every 32×32 region within range of (x, z), with its biome colour. The four town regions are flagged.
export function mapRegions(x, z, range = MAP_RANGE) {
  const out = [], lo = (v) => Math.floor((v - range) / REGION), hi = (v) => Math.floor((v + range) / REGION);
  for (let rx = lo(x); rx <= hi(x); rx++)
    for (let rz = lo(z); rz <= hi(z); rz++)
      out.push({ rx, rz, colour: biomeAt(rx * REGION, rz * REGION).colour, home: homeRegion(rx, rz) });
  return out;
}

// Slide a far point along its direction from the player until it sits just inside the map's edge.
function toEdge(x, y, edge) {
  const m = Math.max(Math.abs(x), Math.abs(y));
  return m <= edge ? { x, y, far: false } : { x: (x * edge) / m, y: (y * edge) / m, far: true };
}

// A small arrow centred on (x, y), pointing away from the player along angle a.
const arrow = (x, y, a, size, fill) =>
  `<path transform="translate(${num(x)} ${num(y)}) rotate(${num(deg(a) + 90)})" d="M0 ${num(-size)} L${num(size * 0.82)} ${num(size * 0.53)} L0 ${num(size * 0.2)} L${num(-size * 0.82)} ${num(size * 0.53)}Z" fill="${fill}" stroke="${INK}" stroke-width=".5"/>`;

// The inside of the HUD minimap (80 units across): learning gems, home and friends. A friend within range
// is a blue circle with their initial; anyone further away is an arrow at radius 35 pointing their way,
// with the initial beside it, so a child always knows which way a friend is. No position yet: not drawn.
// A friend right beside you sits just next to your marker (drawn on top of these), so their letter shows.
export function minimapMarksHTML({ p, stations = [], friends = [], range = 38 } = {}) {
  const near = (o) => Math.abs(o.x - p.x) < range && Math.abs(o.z - p.z) < range;
  let html = '';
  for (const s of stations)
    if (placed(s) && near(s))
      html += `<path d="M${num(p.x - s.x)} ${num(p.z - s.z - 2.4)} l2.4 2.4 -2.4 2.4 -2.4 -2.4Z" fill="${esc(s.colour)}" stroke="#fff" stroke-width=".5"/>`;
  if (Math.hypot(p.x, p.z) < range) html += `<text x="${num(p.x)}" y="${num(p.z)}" font-size="7" fill="#fff2ae">⌂</text>`;
  for (const f of friends) {
    if (!placed(f)) continue;
    let cx = p.x - f.x, cy = p.z - f.z;
    const letter = esc(initial(f.name)), d = Math.hypot(cx, cy);
    if (near(f)) {
      if (d < 5) [cx, cy] = d < 0.01 ? [5, 0] : [(cx * 5) / d, (cy * 5) / d];
      html += `<g class="map-friend"><circle cx="${num(cx)}" cy="${num(cy)}" r="3" fill="${FRIEND_COLOUR}" stroke="#fff" stroke-width=".6"/><text x="${num(cx)}" y="${num(cy)}" dy=".35em" font-size="4" font-weight="900" text-anchor="middle" fill="${INK}">${letter}</text></g>`;
      continue;
    }
    const a = Math.atan2(cy, cx), c = Math.cos(a), s = Math.sin(a);
    html += `<g class="map-friend-far">${arrow(35 * c, 35 * s, a, 3.4, FRIEND_COLOUR)}<text x="${num(28 * c)}" y="${num(28 * s)}" dy=".35em" font-size="4.4" font-weight="900" text-anchor="middle" fill="${FRIEND_COLOUR}" stroke="${INK}" stroke-width="1" paint-order="stroke">${letter}</text></g>`;
  }
  return html;
}

// Rough label width (a heavy rounded font is about 0.6 em per letter), to keep labels apart.
const labelWidth = (text, size) => [...text].length * size * 0.6 + 1.5;
const overlap = (a, b) => Math.max(0, Math.min(a.x1, b.x1) - Math.max(a.x0, b.x0)) * Math.max(0, Math.min(a.y1, b.y1) - Math.max(a.y0, b.y0));

// Put each label beside its mark (below, above, right, left, then the corners), wherever it covers no
// other mark or label. Friends go first and are always named (where they cover least); then home, town
// lessons, places and landmarks, nearest first. Any other label with no free spot is left out: the icon
// stays, and the list under the map names every place.
function placeLabels(marks, R) {
  const boxes = marks.map((m) => ({ x0: m.x - m.r, x1: m.x + m.r, y0: m.y - m.r, y1: m.y + m.r, m }));
  const taken = [];
  const order = marks.filter((m) => m.label).sort((a, b) => a.rank - b.rank || Math.hypot(a.x, a.y) - Math.hypot(b.x, b.y));
  for (const m of order) {
    const w = labelWidth(m.label, m.size), h = m.size, gap = m.r + 1, below = m.y + gap + h * 0.8, above = m.y - gap - h * 0.25;
    const spots = [
      [m.x, below, 'middle'],
      [m.x, above, 'middle'],
      [m.x + gap, m.y + h * 0.35, 'start'],
      [m.x - gap, m.y + h * 0.35, 'end'],
      [m.x + m.r * 0.6, below, 'start'],
      [m.x - m.r * 0.6, below, 'end'],
      [m.x + m.r * 0.6, above, 'start'],
      [m.x - m.r * 0.6, above, 'end'],
    ].map(([x, y, anchor]) => {
      const x0 = anchor === 'middle' ? x - w / 2 : anchor === 'start' ? x : x - w;
      return { x, y, anchor, box: { x0, x1: x0 + w, y0: y - h * 0.8, y1: y + h * 0.25 } };
    }).filter((s) => s.box.x0 >= -R && s.box.x1 <= R && s.box.y0 >= -R && s.box.y1 <= R);
    const cost = (s) => taken.reduce((t, b) => t + overlap(s.box, b), 0) + boxes.reduce((t, b) => t + (b.m === m ? 0 : overlap(s.box, b)), 0);
    const ranked = spots.map((s) => ({ s, c: cost(s) }));
    const best = ranked.find((r) => r.c === 0) || (m.always && ranked.length ? ranked.reduce((a, b) => (b.c < a.c ? b : a)) : null);
    if (!best) continue;
    const spot = best.s;
    taken.push(spot.box);
    m.text = spot;
  }
}

const labelHTML = (m) =>
  m.text ? `<text class="map-label" x="${num(m.text.x)}" y="${num(m.text.y)}" font-size="${m.size}" text-anchor="${m.text.anchor}">${esc(m.label)}</text>` : '';
// A generous, invisible circle so small marks are easy to tap.
const hitHTML = (m) => `<circle cx="${num(m.x)}" cy="${num(m.y)}" r="${m.hit}" fill="transparent"/>`;
const glyph = (m, size, fill = '#fff') =>
  `<text x="${num(m.x)}" y="${num(m.y)}" dy=".35em" font-size="${size}" text-anchor="middle" fill="${fill}">${m.icon}</text>`;

function markHTML(m) {
  const at = `cx="${num(m.x)}" cy="${num(m.y)}"`;
  let body = '';
  if (m.kind === 'station')
    body = `<path d="M${num(m.x)} ${num(m.y - 5.5)} l5.5 5.5 -5.5 5.5 -5.5 -5.5Z" fill="${esc(m.colour)}" stroke="#fff" stroke-width=".8"/>${glyph(m, 5.2)}${m.stamped ? `<text class="map-stamp" x="${num(m.x + 5)}" y="${num(m.y - 2.5)}" font-size="5" fill="#b8f3a4">✓</text>` : ''}`;
  else if (m.kind === 'landmark') body = `<circle ${at} r="4.6" fill="#3a2a4a" stroke="#ffe6a5" stroke-width=".8"/>${glyph(m, 5)}`;
  else if (m.kind === 'place')
    body = `<rect x="${num(m.x - 4.5)}" y="${num(m.y - 4.5)}" width="9" height="9" rx="2" fill="#4a3826" stroke="#ffd782" stroke-width=".8"/>${glyph(m, 5)}`;
  else if (m.kind === 'home')
    body = `${m.far ? arrow(m.x + 8 * Math.cos(m.a), m.y + 8 * Math.sin(m.a), m.a, 2.8, '#fff2ae') : ''}<circle ${at} r="5" fill="#5a4a1a" stroke="#fff2ae" stroke-width=".8"/>${glyph(m, 7, '#fff2ae')}`;
  else if (m.kind === 'friend')
    body = `${m.far ? arrow(m.x + 8 * Math.cos(m.a), m.y + 8 * Math.sin(m.a), m.a, 3, FRIEND_COLOUR) : ''}<circle ${at} r="4.6" fill="${FRIEND_COLOUR}" stroke="#fff" stroke-width="1"/><text x="${num(m.x)}" y="${num(m.y)}" dy=".35em" font-size="5.2" font-weight="900" text-anchor="middle" fill="${INK}">${esc(initial(m.label))}</text>`;
  const op = m.op ? ` data-op="${m.op}"` : '';
  return `<g class="${m.cls}" data-act="${m.act}"${op} data-id="${esc(m.id)}" role="button" aria-label="${esc(m.aria)}">${hitHTML(m)}${body}${labelHTML(m)}</g>`;
}

// The journal's drawn map (range 80 → 160 units across): biome tints, trails, the town square, lessons,
// landmarks and town places to walk to, home, friends by name (at the edge when further away), the current
// waypoint ✦ and the player's arrow. Every place and friend is a <g data-act> that sets the sparkle trail.
export function exploreMapHTML({ player, range = MAP_RANGE, regions = [], stations = [], landmarks = [], places = [], friends = [], home = null, waypoint = null } = {}) {
  const p = player, R = range, X = (x) => p.x - x, Z = (z) => p.z - z;
  const shown = (x, y, pad) => Math.abs(x) <= R - pad && Math.abs(y) <= R - pad;
  const marks = [{ kind: 'you', x: 0, y: 0, r: 7 }];
  for (const l of landmarks)
    if (placed(l) && shown(X(l.x), Z(l.z), 5))
      marks.push({ kind: 'landmark', cls: 'map-landmark', act: 'expansion', op: 'trail', id: l.id, icon: esc(l.icon), x: X(l.x), y: Z(l.z), r: 4.6, hit: 7, label: l.label || l.name, size: 4.6, rank: 4, aria: `Show me the way to ${l.name}` });
  for (const pl of places)
    if (placed(pl) && shown(X(pl.x), Z(pl.z), 5))
      marks.push({ kind: 'place', cls: 'map-place', act: 'navigate', id: pl.id, icon: esc(pl.icon), x: X(pl.x), y: Z(pl.z), r: 4.5, hit: 7, label: pl.name, size: 4.6, rank: 3, aria: `Show me the way to ${pl.name}` });
  for (const s of stations)
    if (placed(s) && shown(X(s.x), Z(s.z), 6))
      // Town lessons are labelled like their signs; wilderness gems are everywhere, so they show only their icon.
      marks.push({ kind: 'station', cls: 'map-station', act: 'navigate', id: s.id, icon: esc(s.icon), colour: s.colour, stamped: !!s.stamped, x: X(s.x), y: Z(s.z), r: 5.5, hit: 7, label: inTown(s) ? LESSON_LABELS[s.id] || s.name : '', size: 4.6, rank: 2, aria: `Show me the way to ${s.name}` });
  if (placed(home)) {
    const e = toEdge(X(home.x), Z(home.z), R - 13);
    marks.push({ kind: 'home', cls: 'map-home', act: 'navigate', id: 'home', icon: '⌂', ...e, a: Math.atan2(Z(home.z), X(home.x)), r: 5, hit: 7, label: 'Home', size: 4.6, rank: 1, aria: 'Show me the way home' });
  }
  for (const f of friends)
    if (placed(f)) {
      const name = String(f.name || 'Friend'), fx = X(f.x), fz = Z(f.z), d = Math.hypot(fx, fz);
      // A friend right beside you is drawn just next to your arrow, so neither hides the other.
      const e = d < 10 ? { ...(d < 0.01 ? { x: 10, y: 0 } : { x: (fx * 10) / d, y: (fz * 10) / d }), far: false } : toEdge(fx, fz, R - 13);
      marks.push({ kind: 'friend', cls: `map-friend${e.far ? ' far' : ''}`, act: 'goToFriend', id: f.id, ...e, a: Math.atan2(fz, fx), r: 4.6, hit: 8, label: name, size: 5.6, rank: 0, always: true, aria: `Go to ${name}` });
    }
  placeLabels(marks, R);

  const out = [`<svg class="explore-map" viewBox="${-R} ${-R} ${2 * R} ${2 * R}" role="group" aria-label="Map of places near you">`];
  out.push(`<rect x="${-R}" y="${-R}" width="${2 * R}" height="${2 * R}" fill="#27453a"/>`);
  for (const g of regions)
    if (!g.home)
      out.push(`<rect x="${num(X((g.rx + 1) * REGION))}" y="${num(Z((g.rz + 1) * REGION))}" width="${REGION + 0.3}" height="${REGION + 0.3}" fill="${esc(g.colour)}" fill-opacity=".35"/>`);
  // Trails beyond town follow both axes and every region's middle lines (onTrail in discovery-terrain.js).
  const lines = (v) => {
    const at = [0];
    for (let k = Math.floor((v - R) / REGION) - 1; k <= Math.ceil((v + R) / REGION); k++) at.push(k * REGION + 16);
    return at.filter((w) => Math.abs(v - w) <= R);
  };
  const trails = lines(p.x).map((w) => `M${num(X(w))} ${-R}V${R}`).join('') + lines(p.z).map((w) => `M${-R} ${num(Z(w))}H${R}`).join('');
  if (trails) out.push(`<path class="map-trails" d="${trails}" stroke="${PATH}" stroke-opacity=".7" stroke-width="2.4" fill="none"/>`);
  if (Math.abs(p.x) < R + 32 && Math.abs(p.z) < R + 32) {
    // The town square: the four home regions, the paths out to each lesson and the four exits, the golden
    // building plot and the pond beside the Craft & Trade stall (decorateHome in discovery-world.js).
    const at = (x, z) => `${num(X(x))} ${num(Z(z))}`;
    out.push(`<rect class="map-town" x="${num(X(32))}" y="${num(Z(32))}" width="64" height="64" rx="4" fill="#4f8a47" stroke="#fff2ae" stroke-opacity=".6" stroke-width=".8" stroke-dasharray="3 2"/>`);
    const paths = STATIONS.map((s) => `M${at(0, 0)}L${at(s.x, s.z)}`).join('') + [[26, 32], [-26, -32]].map(([a, b]) => `M${at(a, 0)}L${at(b, 0)}M${at(0, a)}L${at(0, b)}`).join('');
    out.push(`<path d="${paths}" stroke="${PATH}" stroke-width="2.4" stroke-linecap="round" fill="none"/>`);
    out.push(`<rect x="${num(X(PLOT.x1 + 1))}" y="${num(Z(PLOT.z1 + 1))}" width="${PLOT.x1 - PLOT.x0 + 1}" height="${PLOT.z1 - PLOT.z0 + 1}" fill="#ffd23f" fill-opacity=".2" stroke="#ffd23f" stroke-width=".8"/>`);
    out.push(`<rect x="${num(X(-15))}" y="${num(Z(-10))}" width="5" height="5" rx="1.5" fill="#4aa8e8"/>`);
    out.push(`<text class="map-town-name" x="${num(X(32) + 2.5)}" y="${num(Z(32) + 6)}" font-size="4.6">Town</text>`);
  }
  if (placed(waypoint)) {
    const w = toEdge(X(waypoint.x), Z(waypoint.z), R - 9);
    out.push(`<g class="map-waypoint" pointer-events="none"><path d="M0 0L${num(w.x)} ${num(w.y)}" stroke="${GOLD}" stroke-width="1.2" stroke-dasharray="1.5 2.5" stroke-linecap="round" fill="none"/><circle cx="${num(w.x)}" cy="${num(w.y)}" r="7.5" fill="none" stroke="${GOLD}" stroke-width="1.2"/><text x="${num(w.x + 5.5)}" y="${num(w.y - 5.5)}" dy=".35em" font-size="8" text-anchor="middle" fill="${GOLD}" stroke="#5a3d00" stroke-width=".8" paint-order="stroke">✦</text></g>`);
  }
  const draw = (kind) => marks.filter((m) => m.kind === kind).forEach((m) => out.push(markHTML(m)));
  ['station', 'place', 'landmark', 'home'].forEach(draw);
  out.push(`<path class="map-you" pointer-events="none" transform="rotate(${deg(-(p.facing || 0))})" d="M0 -8.5 L5.5 6 L0 3 L-5.5 6Z" fill="#fff" stroke="#13202c" stroke-width="1" stroke-linejoin="round"/>`);
  draw('friend');
  out.push('</svg>');
  return out.join('');
}

// "Friends here" under the map: a button for each friend in this world with how many steps away they are
// ("far away" beyond the map's range). A friend with no position yet can't be chosen.
export function friendsHereHTML({ player, friends = [], range = MAP_RANGE } = {}) {
  const list = (Array.isArray(friends) ? friends : []).filter((f) => f && f.id != null);
  if (!list.length) return '';
  const rows = list.map((f) => {
    const name = esc(f.name || 'Friend');
    if (!placed(f)) return `<button class="btn btn-small btn-dark" disabled>🙂 ${name} is still arriving…</button>`;
    const d = Math.round(Math.hypot(f.x - player.x, f.z - player.z));
    return `<button class="btn btn-small btn-blue" data-act="goToFriend" data-id="${esc(f.id)}">🙂 Go to ${name} · ${d > range ? 'far away' : steps(d)}</button>`;
  });
  return `<div class="journal-friends"><strong>Friends here</strong><div>${rows.join('')}</div></div>`;
}
